import { useState, type ReactNode } from 'react';
import { Check, Plus, X, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface MultiPopoverOption {
  id: number;
  name: string;
  /** Draws a colour dot before the name (labels). */
  color?: string;
  /** Anything else before the name (e.g. a person's avatar). Wins over `color`. */
  leading?: ReactNode;
}

const ROW = 'h-7 py-0';

/**
 * A compact pill that opens a searchable multi-select list — the work-item
 * Assignees / Labels pickers. Built on cmdk: type to filter, ↑/↓ to move,
 * Enter to toggle, Esc to close. Options selected when the list opens float to
 * the top; that order holds while it stays open, so a row never jumps out from
 * under the pointer.
 *
 * For a form field that shows the choices as chips, use `MultiSelect` instead.
 */
export function MultiPopover({
  icon: Icon,
  label,
  count,
  options,
  selected,
  onToggle,
  onClear,
  preview,
  onCreate,
  creating,
  emptyText = 'None available.',
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  options: MultiPopoverOption[];
  selected: number[];
  onToggle: (id: number) => void;
  /** Adds a "Clear" row while anything is selected. */
  onClear?: () => void;
  /** Shown inside the trigger when there's a selection (e.g. assignee avatars)
   *  instead of the "· N" count. */
  preview?: ReactNode;
  /** When set, a query matching no option offers to create it (e.g. a
   *  project-wide label); Enter creates. */
  onCreate?: (name: string) => void;
  creating?: boolean;
  /** Shown when there are no options at all (a query with no match says "No match."). */
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pinned, setPinned] = useState<number[]>([]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) setPinned(selected);
    else setQuery('');
  }

  const q = query.trim().toLowerCase();
  const ordered = [
    ...options.filter((o) => pinned.includes(o.id)),
    ...options.filter((o) => !pinned.includes(o.id)),
  ];
  const filtered = q ? ordered.filter((o) => o.name.toLowerCase().includes(q)) : ordered;
  const exists = options.some((o) => o.name.toLowerCase() === q);
  const canCreate = Boolean(onCreate) && q.length > 0 && !exists && !creating;

  function submitCreate() {
    if (!canCreate) return;
    onCreate!(query.trim());
    setQuery('');
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          <Icon className="size-3.5" />
          {/* A preview (e.g. assignee avatars) replaces the label entirely;
              otherwise show the label + optional count. */}
          {count > 0 && preview ? (
            preview
          ) : (
            <>
              <span>{label}</span>
              {count > 0 ? <span className="text-muted-foreground">· {count}</span> : null}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-1">
        {/* cmdk labels its input from Command's `label`. */}
        <Command shouldFilter={false} loop label={`Search ${label.toLowerCase()}`}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={onCreate ? `Search or add ${label.toLowerCase()}…` : `Search ${label.toLowerCase()}…`}
            className="h-8"
          />
          <CommandList className="max-h-56" label={label}>
            {filtered.length === 0 && !canCreate ? (
              <p className="text-muted-foreground px-2 py-2 text-xs">{q ? 'No match.' : emptyText}</p>
            ) : null}
            <CommandGroup className="p-0 pt-1">
              {/* First, so Enter on a new name creates it rather than toggling a
                  partial match — as before the list became keyboard-driven. */}
              {canCreate ? (
                <CommandItem value="__create__" onSelect={submitCreate} className={ROW}>
                  <Plus className="size-4" />
                  <span className="flex-1 truncate">Create “{query.trim()}”</span>
                </CommandItem>
              ) : null}
              {filtered.map((o) => {
                const isSelected = selected.includes(o.id);
                return (
                  <CommandItem
                    key={o.id}
                    value={`option-${o.id}`}
                    onSelect={() => onToggle(o.id)}
                    // cmdk's aria-selected marks the active row; the choice
                    // itself is aria-checked.
                    aria-checked={isSelected}
                    className={ROW}
                  >
                    {o.leading ??
                      (o.color !== undefined ? (
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: o.color || '#6366f1' }}
                        />
                      ) : null)}
                    <span className="flex-1 truncate">{o.name}</span>
                    {isSelected ? (
                      <>
                        <Check aria-hidden className="text-foreground size-4" />
                        <span className="sr-only">, selected</span>
                      </>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {onClear && selected.length > 0 ? (
              <>
                <CommandSeparator className="my-1" />
                <CommandGroup className="p-0">
                  <CommandItem value="__clear__" onSelect={onClear} className={ROW}>
                    <X className="size-4" />
                    <span className="flex-1">Clear</span>
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
